import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

interface SlideUpState {
  [panelId: string]: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SlideUpService {
  private stateSubject = new BehaviorSubject<SlideUpState>({});
  public state$: Observable<SlideUpState> = this.stateSubject.asObservable();

  /**
   * Toggle a slide-up panel by ID
   * @param panelId - Unique identifier for the panel
   */
  toggle(panelId: string): void {
    const currentState = this.stateSubject.value;
    const isOpen = currentState[panelId] || false;

    this.stateSubject.next({
      ...currentState,
      [panelId]: !isOpen,
    });
  }

  /**
   * Open a slide-up panel by ID
   * @param panelId - Unique identifier for the panel
   */
  open(panelId: string): void {
    const currentState = this.stateSubject.value;

    if (!currentState[panelId]) {
      this.stateSubject.next({
        ...currentState,
        [panelId]: true,
      });
    }
  }

  /**
   * Close a slide-up panel by ID
   * @param panelId - Unique identifier for the panel
   */
  close(panelId: string): void {
    const currentState = this.stateSubject.value;

    if (currentState[panelId]) {
      this.stateSubject.next({
        ...currentState,
        [panelId]: false,
      });
    }
  }

  /**
   * Check if a panel is currently open
   * @param panelId - Unique identifier for the panel
   * @returns true if panel is open, false otherwise
   */
  isOpen(panelId: string): boolean {
    return this.stateSubject.value[panelId] || false;
  }

  /**
   * Get an observable for a specific panel's state
   * @param panelId - Unique identifier for the panel
   * @returns Observable<boolean> that emits true when open, false when closed
   */
  getPanelState(panelId: string): Observable<boolean> {
    return new Observable((observer) => {
      const subscription = this.state$.subscribe((state) => {
        observer.next(state[panelId] || false);
      });

      return () => subscription.unsubscribe();
    });
  }

  /**
   * Close all open panels
   */
  closeAll(): void {
    this.stateSubject.next({});
  }
}
